/**
 * useMetadataFetch Hook
 *
 * Fetches metadata for an API endpoint using React Query.
 * Supports both write model (/metadata) and view model (/metadata-read) endpoints.
 *
 * This hook is designed for the self-contained MetadataTable component,
 * abstracting away the metadata fetching logic.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { metadataService } from '@/services/metadataService'
import type { FieldMetadata, FormSection, DisplayMetadata, DefaultFilter, DefaultSort, PresetsMetadata, SearchConfig, LifecycleMetadata, ApiConfig, RecordActionsMetadata, ComparisonConfig } from '@/services/metadataService'
import { getAllColumnOrder } from '../utils/columnSorting'
import type { UseMetadataFetchResult } from '../types/selfContained.types'

// Stable empty references to prevent infinite re-render loops
// Without these, `metadataResponse?.fields ?? {}` creates new objects every render,
// which invalidates useMemo dependencies and triggers useEffect cascades
const EMPTY_FIELDS: Record<string, FieldMetadata> = {}
const EMPTY_SECTIONS: FormSection[] = []
const EMPTY_BASE_FILTERS: DefaultFilter[] = []
const EMPTY_DEFAULT_FILTERS: DefaultFilter[] = []
const EMPTY_DEFAULT_SORT: DefaultSort[] = []

/**
 * Options for useMetadataFetch hook
 */
interface UseMetadataFetchOptions {
  /**
   * API endpoint path (e.g., '/offerings', '/audit_logs')
   */
  endpoint: string

  /**
   * Whether to use view metadata endpoint (/metadata-read) instead of primary (/metadata).
   * Use this when the backend has a read service with enriched/different fields.
   */
  useViewMetadata?: boolean

  /**
   * Whether to enable the query (default: true).
   * Set to false to disable fetching.
   */
  enabled?: boolean
}

/**
 * Hook to fetch metadata for an API endpoint.
 *
 * @param options - Configuration options
 * @returns Metadata fields, columns, and loading state
 *
 * @example Basic usage
 * ```tsx
 * const {
 *   fields,
 *   availableColumns,
 *   isLoading,
 *   error,
 * } = useMetadataFetch({ endpoint: '/offerings' })
 * ```
 *
 * @example With view metadata
 * ```tsx
 * const metadata = useMetadataFetch({
 *   endpoint: '/audit_logs',
 *   useViewMetadata: true, // Uses /metadata-read endpoint
 * })
 * ```
 */
export function useMetadataFetch({
  endpoint,
  useViewMetadata = false,
  enabled = true,
}: UseMetadataFetchOptions): UseMetadataFetchResult {
  // Fetch metadata using React Query
  // If useViewMetadata is true, try /metadata-read first, fall back to /metadata if it fails
  // IMPORTANT: When using view metadata, also fetch lifecycle from write endpoint
  // because view services don't have StatefulServiceMixin (no lifecycle config)
  const {
    data: metadataResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['metadata', endpoint, useViewMetadata ? 'read' : 'write'],
    queryFn: async () => {
      if (useViewMetadata) {
        // Try metadata-read first, fall back to metadata if not available
        // Use suppressErrors: true so failed probe doesn't show error modal to user
        const readResponse = await metadataService.getMetadataByEndpoint(endpoint, true, { suppressErrors: true })
        if (readResponse.success && readResponse.data) {
          // View service succeeded - also fetch write service for service-level configs
          // View services don't have StatefulServiceMixin, so they lack lifecycle actions
          // They also don't have api, record_actions, or comparison configs
          const writeResponse = await metadataService.getMetadataByEndpoint(endpoint, false, { suppressErrors: true })
          if (writeResponse.success && writeResponse.data) {
            // Deep merge utility - view wins at every level, write fills gaps
            // This allows views to inherit nested configuration from write services
            // while being able to override specific nested properties
            const deepMerge = <T extends object>(write?: T, view?: T): T | undefined => {
              if (!write && !view) return undefined
              if (!write) return view
              if (!view) return write

              const result = { ...write } as T
              for (const key of Object.keys(view) as Array<keyof T>) {
                const viewVal = view[key]
                const writeVal = write[key]

                // If both are plain objects (not arrays, not null), recurse
                if (
                  viewVal && typeof viewVal === 'object' && !Array.isArray(viewVal) &&
                  writeVal && typeof writeVal === 'object' && !Array.isArray(writeVal)
                ) {
                  result[key] = deepMerge(writeVal as object, viewVal as object) as T[keyof T]
                } else {
                  // Otherwise view wins (including arrays - view's array replaces write's)
                  result[key] = viewVal
                }
              }
              return result
            }

            // Fallback helper - view wins if defined, otherwise use write
            // Used for arrays which are "all or nothing" (can't sensibly merge array elements)
            const fallback = <T>(view?: T, write?: T): T | undefined => view ?? write

            // Merge service-level configs from write endpoint into view metadata
            // These configs are defined on the write service but needed for UI rendering:
            // - display: UI labels, icons, descriptions, title_fields (deep merged)
            // - comparison: record comparison feature settings (deep merged)
            // - api: endpoint availability (deep merged)
            // - presets: quick filter buttons (deep merged - view can override write's presets)
            // - search: search configuration (deep merged)
            // - form_sections: array - view wins entirely if defined, else use write
            // - base_filters: array - view wins entirely if defined, else use write
            // - default_filters: array - view wins entirely if defined, else use write
            // - default_sort: array - view wins entirely if defined, else use write
            // - lifecycle: state machine actions (write-only, view never defines)
            // - record_actions: delete/undelete/purge UI config (write-only, view never defines)
            return {
              ...readResponse.data,

              // Deep merge objects (view overrides nested properties, write fills gaps)
              display: deepMerge(writeResponse.data.display, readResponse.data.display),
              comparison: deepMerge(writeResponse.data.comparison, readResponse.data.comparison),
              api: deepMerge(writeResponse.data.api, readResponse.data.api),
              presets: deepMerge(writeResponse.data.presets, readResponse.data.presets),
              search: deepMerge(
                writeResponse.data.search as object | undefined,
                readResponse.data.search as object | undefined
              ) as SearchConfig | false | undefined,

              // Fallback for arrays (view wins entirely if defined, else use write)
              form_sections: fallback(readResponse.data.form_sections, writeResponse.data.form_sections),
              base_filters: fallback(readResponse.data.base_filters, writeResponse.data.base_filters),
              default_filters: fallback(readResponse.data.default_filters, writeResponse.data.default_filters),
              default_sort: fallback(readResponse.data.default_sort, writeResponse.data.default_sort),

              // Write-only configs (view services don't have these mixins)
              ...(writeResponse.data.lifecycle && { lifecycle: writeResponse.data.lifecycle }),
              ...(writeResponse.data.record_actions && { record_actions: writeResponse.data.record_actions }),
            }
          }
          return readResponse.data
        }
        // Fall back to regular metadata endpoint
        console.debug(`[useMetadataFetch] /metadata-read not available for ${endpoint}, falling back to /metadata`)
        const fallbackResponse = await metadataService.getMetadataByEndpoint(endpoint, false)
        if (!fallbackResponse.success || !fallbackResponse.data) {
          throw new Error(fallbackResponse.message || 'Failed to fetch metadata')
        }
        return fallbackResponse.data
      }

      // Standard metadata fetch
      const response = await metadataService.getMetadataByEndpoint(endpoint, false)
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch metadata')
      }
      return response.data
    },
    enabled,
    staleTime: 1000 * 60 * 60, // 1 hour - metadata changes rarely
  })

  // Extract fields from response with stable references
  // CRITICAL: Use stable empty constants to prevent infinite re-render loops
  // when metadataResponse is undefined or missing properties
  const fields = useMemo<Record<string, FieldMetadata>>(
    () => metadataResponse?.fields ?? EMPTY_FIELDS,
    [metadataResponse]
  )
  const formSections = useMemo<FormSection[]>(
    () => metadataResponse?.form_sections ?? EMPTY_SECTIONS,
    [metadataResponse]
  )
  const display: DisplayMetadata | undefined = metadataResponse?.display
  const baseFilters = useMemo<DefaultFilter[]>(
    () => metadataResponse?.base_filters ?? EMPTY_BASE_FILTERS,
    [metadataResponse]
  )
  const defaultFilters = useMemo<DefaultFilter[]>(
    () => metadataResponse?.default_filters ?? EMPTY_DEFAULT_FILTERS,
    [metadataResponse]
  )
  const defaultSort = useMemo<DefaultSort[]>(
    () => metadataResponse?.default_sort ?? EMPTY_DEFAULT_SORT,
    [metadataResponse]
  )
  const presets: PresetsMetadata | undefined = metadataResponse?.presets
  const searchConfig: SearchConfig | false | undefined = metadataResponse?.search
  const emptyMessage: string | undefined = metadataResponse?.display?.empty_message
  const lifecycle: LifecycleMetadata | undefined = metadataResponse?.lifecycle
  const api: ApiConfig | undefined = metadataResponse?.api
  const recordActions: RecordActionsMetadata | undefined = metadataResponse?.record_actions
  const comparison: ComparisonConfig | undefined = metadataResponse?.comparison

  // Calculate available columns in proper order
  const availableColumns = useMemo(
    () => getAllColumnOrder(fields, formSections),
    [fields, formSections]
  )

  return {
    fields,
    availableColumns,
    isLoading,
    error: error as Error | null,
    formSections,
    display,
    baseFilters,
    defaultFilters,
    defaultSort,
    presets,
    searchConfig,
    emptyMessage,
    lifecycle,
    api,
    recordActions,
    comparison,
    refetch,
  }
}
